<template>
  <el-dialog v-model="visible" title="专利详情"  :close-on-click-modal="false" :close-on-press-escape="true" fullscreen :show-close="false">
    <div style="height: 100%; width: 100%; display: flex; justify-content: center; align-items: center; overflow: auto;">
      <vue-pdf-embed 
        :source="pdfSource" 
        :page="currentPage" 
        @rendered="handlePageRendered"
        @loading="handleLoading" 
        @error="handleError" 
        :style="{ 
          width: '80%', 
          height: '90%', 
          maxWidth: '1200px',
          maxHeight: '95vh',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }" />
    </div>
    <div v-if="totalPages > 0" style="position: absolute; bottom: 20px; left: 50%; transform: translateX(-50%); 
         background: rgba(0, 0, 0, 0.7); color: white; padding: 10px 20px; border-radius: 20px; display: flex; align-items: center;">
      <el-button @click="prevPage" :disabled="currentPage <= 1" size="small" style="margin-right: 10px;">上一页</el-button>
      <span>第 {{ currentPage }} 页 / 共 {{ totalPages }} 页</span>
      <el-button @click="nextPage" :disabled="currentPage >= totalPages" size="small" style="margin-left: 10px;">下一页</el-button>
    </div>
  </el-dialog>
</template>
<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import VuePdfEmbed from 'vue-pdf-embed'
import * as pdfjsLib from 'pdfjs-dist'
import { ElMessage } from 'element-plus'
import { patentPdfSearch } from '@/api/process'
import { useRoute } from 'vue-router' 

// 定义组件的props和emits
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  data: {
    type: Object,
    default: () => ({})
  },
  projectId: {
    type: String,
    default: ''
  }
})
const emit = defineEmits(['update:modelValue', 'close', 'save'])

// 响应式数据
const visible = ref(true)
const route = useRoute()
const featureId = ref('')
const pdfDocument = ref(null)
const loading = ref(false)
featureId.value = route.query.id
console.log('Feature ID:', featureId.value)

const getfeatureInfo = async () => {
  loading.value = false
  if (!featureId.value) {
    ElMessage.warning('未提供专利号')
    return
  }
  
  try {
    console.log('正在获取PDF，专利号:', featureId.value)
    const data = {
      "pn": featureId.value
    }
    
    const res = await patentPdfSearch(data)
    console.log('PDF响应:', res)
    
    if (res) {
      try {
        // 将文件流转换为Blob对象
        const uint8Array = new Uint8Array(res)
        const blob = new Blob([uint8Array], { type: 'application/pdf' })
        // 生成Blob URL
        pdfSource.value = URL.createObjectURL(blob)
        console.log('PDF Blob URL已生成:', pdfSource.value)
        await initPdf()
        loading.value = true
      } catch (error) {
        ElMessage.error('PDF文件转换失败')
        console.error('PDF转换错误:', error)
      }
    } else {
      ElMessage.error('获取专利详情失败')
    }
  } catch (error) {
    ElMessage.error('获取专利详情失败: ' + error.message)
    console.error('获取专利详情失败:', error)
  }
}

// 关闭弹框
const handleClose = () => {
  visible.value = false
  emit('close')
}

onMounted(() => {
  try {
    // 设置PDF worker路径
    pdfjsLib.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.js`
    console.log('PDF worker路径:', pdfjsLib.GlobalWorkerOptions.workerSrc)

    // 添加错误处理
    if (typeof window.Worker === 'undefined') {
      console.warn('Web Workers are not supported in this environment')
      // 降级使用 fake worker
      pdfjsLib.GlobalWorkerOptions.disableWorker = true
    }
    
    nextTick(() => {
      getfeatureInfo()
    })
  } catch (error) {
    console.error('PDF worker initialization failed:', error)
    // 降级使用 fake worker
    pdfjsLib.GlobalWorkerOptions.disableWorker = true
  }
})

onUnmounted(() => {
  // 清理Blob URL，防止内存泄漏
  if (pdfSource.value) {
    URL.revokeObjectURL(pdfSource.value)
  }
})

const pdfSource = ref('')
const currentPage = ref(1)
const totalPages = ref(0)

// 初始化PDF文档
const initPdf = async () => {
  try {
    if (!pdfSource.value) {
      console.warn('PDF源未设置')
      return
    }
    
    console.log('正在加载PDF文档:', pdfSource.value)
    const loadingTask = pdfjsLib.getDocument(pdfSource.value)
    pdfDocument.value = await loadingTask.promise
    currentPage.value = 1
    totalPages.value = pdfDocument.value.numPages
    console.log('PDF加载完成，总页数:', totalPages.value)
  } catch (error) {
    console.error('加载PDF失败:', error)
    ElMessage.error('PDF加载失败: ' + (error?.message || '未知错误'))
  }
}

const handlePageRendered = () => {
  console.log('当前页面渲染完成:', currentPage.value)
}

const handleLoading = (isLoading) => {
  console.log('PDF加载状态:', isLoading)
}

const handleError = (error) => {
  console.error('PDF错误:', error)
  ElMessage.error('PDF加载失败: ' + (error?.message || '未知错误'))
}

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
  }
}
</script>
<style scoped>
/* PDF预览容器样式 */
.pdf-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow: auto;
}

/* PDF页面样式 */
:deep(.vue-pdf-embed) {
  width: 80%;
  height: 90%;
  max-width: 1200px;
  max-height: 95vh;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin: 0 auto;
}

:deep(.vue-pdf-embed canvas) {
  max-width: 100%;
  height: auto !important;
  margin: 0 auto;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.dialog-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.dialog-actions {
  display: flex;
  gap: 8px;
  margin-right: 40px;
}

.mermaid-content {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: auto;
}

.chart-section {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background-color: #fafafa;
  overflow: auto;
  min-height: 400px;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #909399;
}

.error-container {
  color: #F56C6C;
}

.chart-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow: auto;
}

/* 确保SVG图表正确显示 */
.chart-container :deep(svg) {
  max-width: 100%;
  max-height: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
}

/* Mermaid图表特定样式 */
.chart-container :deep(.mermaid-chart) {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-container :deep(.mermaid-chart svg) {
  max-width: 100%;
  max-height: 100%;
}

.title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.content {
  font-size: 14px;
  color: #606266;
}

/* 页面导航按钮样式 */
.page-navigation {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 10px 20px;
  border-radius: 20px;
  display: flex;
  align-items: center;
}

.page-navigation .el-button {
  margin: 0 10px;
}
</style>